/**
 * The links a committed command handed back (a card checkout, a supplier's file links, a testing preview's sign-in),
 * each under its label with its own Open (in a new tab) and Copy, and all of them at once when there are several. Worded as Classic's file links. The links live in
 * the shell's state only: never stored, never put in the URL or the log.
 */
import { useState } from "react";
import { Copy, ExternalLink } from "lucide-react";
import { usePlatformCapabilities } from "@shared-core/capabilities/context";
import {
  allLinksText,
  linkExpiry,
} from "@shared-core/domain/object-ui/command-links";
import type { CommandLink } from "@shared-core/domain/object-ui/contract";
import { Button } from "../../primitives/button";

export function CommandResultLinks({ links }: { links: CommandLink[] }) {
  const capabilities = usePlatformCapabilities();
  const [copied, setCopied] = useState<ReadonlySet<number>>(new Set());
  const [copyFailed, setCopyFailed] = useState(false);
  const several = links.length > 1;

  const copy = async (text: string, indexes: number[]) => {
    // A failed copy leaves the links on screen, so the person can copy again.
    try {
      await capabilities.copyToClipboard(text);
      setCopied((current) => new Set([...current, ...indexes]));
      setCopyFailed(false);
    } catch {
      setCopyFailed(true);
    }
  };

  return (
    <div
      className="space-y-2 rounded-md border border-border bg-muted/40 p-3"
      aria-label={several ? "Links" : "Link"}
      role="group"
    >
      {copyFailed && (
        <p role="alert" className="text-sm text-red-600">
          The browser didn't copy the link. Select Copy beside the link to try
          again.
        </p>
      )}
      <ul className="space-y-1">
        {links.map((link, index) => {
          const expiry = linkExpiry(link);
          return (
            <li
              key={link.href}
              className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2"
            >
              <span className="min-w-0 truncate text-sm">
                {link.label}
                {expiry && (
                  <span className="text-muted-foreground"> · {expiry}</span>
                )}
              </span>
              <span className="flex gap-1">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-7 px-2"
                  aria-label={`Open link for ${link.label}`}
                  onClick={() => capabilities.openExternalUrl(link.href)}
                >
                  <ExternalLink aria-hidden className="size-3.5" />
                  Open
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-7 px-2"
                  aria-label={`Copy link for ${link.label}`}
                  onClick={() => void copy(link.href, [index])}
                >
                  <Copy aria-hidden className="size-3.5" />
                  {copied.has(index) ? "Copied" : "Copy"}
                </Button>
              </span>
            </li>
          );
        })}
      </ul>
      {several && (
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-7 px-2"
          onClick={() =>
            void copy(
              allLinksText(links),
              links.map((_, index) => index),
            )
          }
        >
          <Copy aria-hidden className="size-3.5" />
          Copy all links
        </Button>
      )}
    </div>
  );
}
